/**
 * WeblabsFilterPanel — the top filter bar for the Weblabs dashboard.
 *
 * Owns the SERVER-SIDE bounds (Apply-gated, they re-query): the Metric Day
 * Range (start/end) — filtered on `day`, the table's partition column, so
 * Athena prunes partitions and stays fast — plus the Marketplace filter
 * (country is summed away in the aggregation, so it can't be a client
 * dimension). The x-axis Group by toggle is client-side (instant). The
 * remaining filters (activation state / type / start-launch date) and the
 * SB/SP toggle are client-side in WeblabsContent on the aggregated rows.
 */

import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type { PluginFilterPanelProps } from "../../types";
import type { Grouping } from "./weblabsChart";

interface WeblabsFilterPanelProps extends PluginFilterPanelProps {
  /** Current x-axis grouping (client-side; changes apply instantly). */
  grouping?: Grouping;
  onGroupingChange?: (g: Grouping) => void;
  /** Distinct marketplaces for the range (from useWeblabsData). */
  countryOptions?: string[];
}

const WeblabsFilterPanel: React.FC<WeblabsFilterPanelProps> = ({
  filters,
  onFiltersChange,
  onApply,
  isLoading,
  grouping = "D",
  onGroupingChange,
  countryOptions = [],
}) => {
  const update = (partial: Record<string, any>) =>
    onFiltersChange({ ...filters, ...partial });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        mb: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Metric Date Range
      </Typography>
      <TextField
        label="Start"
        type="date"
        size="small"
        value={filters.startDate ?? ""}
        onChange={(e) => update({ startDate: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
      <Typography variant="body2" color="text.secondary">
        to
      </Typography>
      <TextField
        label="End"
        type="date"
        size="small"
        value={filters.endDate ?? ""}
        onChange={(e) => update({ endDate: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="weblabs-marketplace-label">Marketplace</InputLabel>
        <Select
          labelId="weblabs-marketplace-label"
          label="Marketplace"
          value={filters.country ?? ""}
          onChange={(e: SelectChangeEvent) =>
            update({ country: e.target.value })
          }
        >
          <MenuItem value="">All</MenuItem>
          {countryOptions.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        size="small"
        onClick={onApply}
        disabled={isLoading || !filters.startDate || !filters.endDate}
        startIcon={
          isLoading ? <CircularProgress size={16} color="inherit" /> : undefined
        }
      >
        {isLoading ? "Loading…" : "Apply"}
      </Button>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Group by
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grouping}
          onChange={(_, g) => g && onGroupingChange?.(g as Grouping)}
        >
          <ToggleButton value="D">D</ToggleButton>
          <ToggleButton value="W">W</ToggleButton>
          <ToggleButton value="M">M</ToggleButton>
          <ToggleButton value="Q">Q</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export default WeblabsFilterPanel;
